import { useEffect, useState } from "react";
import StepLayout from "./StepLayout";
import NavFooter from "./NavFooter";
import styles from "./StepPreScreen.module.css";

const CONDITIONS = [
  "Diabetes",
  "Hypertension",
  "Thyroid",
  "Asthma",
  "Heart Disease",
  "PCOS",
  "Other",
];

const SURGERY_WHEN = [
  "< 1 month ago",
  "1–3 months ago",
  "3–6 months ago",
  "6–12 months ago",
  "More than a year",
];

export default function StepPreScreen({
  patient,
  initial,
  onNext,
  onBack
}) {
  const [currentStep, setCurrentStep] = useState(0);

  const [hasCondition, setHasCondition] = useState(null);
  const [diagnosed, setDiagnosed] = useState([]);
  const [takingMeds, setTakingMeds] = useState(null);
  const [hasAllergy, setHasAllergy] = useState(null);
  const [hasSurgery, setHasSurgery] = useState(null);
  const [surgeryWhen, setSurgeryWhen] = useState(null);

  /* ---------------- LOAD FROM PARENT STATE ---------------- */

  useEffect(() => {
    if (!initial) return;

    setHasCondition(initial.hasCondition ?? null);
    setDiagnosed(initial.diagnosed ?? []);
    setTakingMeds(initial.takingMeds ?? null);
    setHasAllergy(initial.hasAllergy ?? null);
    setHasSurgery(initial.hasSurgery ?? null);
    setSurgeryWhen(initial.surgeryWhen ?? null);

    /* restore current step */

    if (!initial.hasCondition) {
      setCurrentStep(0);
    } else if (!initial.takingMeds) {
      setCurrentStep(1);
    } else if (!initial.hasAllergy) {
      setCurrentStep(2);
    } else {
      setCurrentStep(3);
    }
  }, [initial]);

  /* ---------------- MULTI SELECT ---------------- */

  const toggleCondition = (condition) => {
    setDiagnosed((prev) => {
      if (prev.includes(condition)) {
        return prev.filter((c) => c !== condition);
      }

      return [...prev, condition];
    });
  };

  /* ---------------- NEXT ---------------- */

  const handleNext = () => {
    if (currentStep === 0) {
      if (!hasCondition) return;

      if (
        hasCondition === "Yes" &&
        diagnosed.length === 0
      )
        return;
    }

    if (currentStep === 1 && !takingMeds) return;

    if (currentStep === 2 && !hasAllergy) return;

    if (currentStep === 3) {
      if (!hasSurgery) return;

      if (
        hasSurgery === "Yes" &&
        !surgeryWhen
      )
        return;

      onNext({
        hasCondition,
        diagnosed,
        takingMeds,
        hasAllergy,
        hasSurgery,
        surgeryWhen,
      });

      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  /* ---------------- BACK ---------------- */

  // const handleBack = () => {
  //   setCurrentStep((prev) =>
  //     Math.max(prev - 1, 0)
  //   );
  // };

  const handleBack = () => {
  if (currentStep === 0) {
    onBack?.();
    return;
  }

  setCurrentStep((prev) => Math.max(prev - 1, 0));
};

// Compute this before the return statement
const isNextDisabled = () => {
  if (currentStep === 0) {
    if (!hasCondition) return true;
    if (hasCondition === "Yes" && diagnosed.length === 0) return true;
  }
  if (currentStep === 1 && !takingMeds) return true;
  if (currentStep === 2 && !hasAllergy) return true;
  if (currentStep === 3) {
    if (!hasSurgery) return true;
    if (hasSurgery === "Yes" && !surgeryWhen) return true;
  }
  return false;
};

    /* ---------------- SAVE TO LOCAL STORAGE ---------------- */
  useEffect(() => {
    const data = {
      hasCondition,
      diagnosed,
      takingMeds,
      hasAllergy,
      hasSurgery,
      surgeryWhen,
    };

    localStorage.setItem("medicalHistory", JSON.stringify(data));
  }, [
    hasCondition,
    diagnosed,
    takingMeds,
    hasAllergy,
    hasSurgery,
    surgeryWhen,
  ]);

  return (
    <StepLayout
      title="Pre-Screening Questions"
      subtitle="Help us understand your medical background"
      icon="📋"
      patient={patient}
      footer={
        <NavFooter
           disableNext={isNextDisabled()}  
          showBack={true}
          onBack={handleBack}
          onNext={handleNext}
          nextLabel={
            currentStep === 3
              ? "Start Assessment"
              : "Continue"
          }
        />
      }
    >
      <div className={styles.prescreenWrapper}>
        <div className={styles.prescreenCard}>
          {/* STEP 1 */}

          {currentStep === 0 && (
            <Section
              title="Chronic Conditions"
              icon="🫀"
            >
              <QRow
                text="Do you have any ongoing medical conditions?"
                options={["Yes", "No"]}
                value={hasCondition}
                onChange={(v) => {
                  setHasCondition(v);

                  if (v === "No") {
                    setDiagnosed([]);
                  }
                }}
              />

              {hasCondition === "Yes" && (
                <ExpandRow label="Select all that apply:">
                  {CONDITIONS.map((c) => (
                    <Chip
                      key={c}
                      label={c}
                      selected={diagnosed.includes(c)}
                      onClick={() =>
                        toggleCondition(c)
                      }
                    />
                  ))}
                </ExpandRow>
              )}
            </Section>
          )}

          {/* STEP 2 */}

          {currentStep === 1 && (
            <Section
              title="Medication History"
              icon="💊"
            >
              <QRow
                text="Are you currently taking any medications?"
                options={["Yes", "No"]}
                value={takingMeds}
                onChange={setTakingMeds}
              />
            </Section>
          )}

          {/* STEP 3 */}

          {currentStep === 2 && (
            <Section
              title="Allergy History"
              icon="🌿"
            >
              <QRow
                text="Do you have any known allergies?"
                options={[
                  "Yes",
                  "No",
                  "Don't Know",
                ]}
                value={hasAllergy}
                onChange={setHasAllergy}
              />
            </Section>
          )}

          {/* STEP 4 */}

          {currentStep === 3 && (
            <Section
              title="Surgical History"
              icon="🏥"
            >
              <QRow
                text="Any previous surgeries or hospitalizations?"
                options={[
                  "Yes",
                  "No",
                  "Don't Know",
                ]}
                value={hasSurgery}
                onChange={(v) => {
                  setHasSurgery(v);

                  if (v !== "Yes") {
                    setSurgeryWhen(null);
                  }
                }}
              />

              {hasSurgery === "Yes" && (
                <ExpandRow label="When did it happen?">
                  {SURGERY_WHEN.map((w) => (
                    <Chip
                      key={w}
                      label={w}
                      selected={surgeryWhen === w}
                      onClick={() =>
                        setSurgeryWhen(w)
                      }
                    />
                  ))}
                </ExpandRow>
              )}
            </Section>
          )}
        </div>
      </div>
    </StepLayout>
  );
}

/* ---------- SMALL COMPONENTS ---------- */

function Section({ title, icon, children }) {
  return (
    <div className={styles.prescreenSection}>
      <div
        className={`ava-text-subheading ${styles.prescreenHeader}`}
      >
        <span className={styles.prescreenIcon}>
          {icon}
        </span>

        <h3 className={styles.prescreenTitle}>
          {title}
        </h3>
      </div>

      {children}
    </div>
  );
}

function QRow({
  text,
  options,
  value,
  onChange,
}) {
  return (
    <div className={styles.prescreenRow}>
      <p
        className={`ava-text-body ${styles.prescreenQuestion}`}
      >
        {text}
      </p>

      <div className={styles.prescreenChips}>
        {options.map((opt) => (
          <Chip
            key={opt}
            label={opt}
            selected={value === opt}
            onClick={() =>
              onChange(value === opt ? null : opt)
            }
          />
        ))}
      </div>
    </div>
  );
}

function ExpandRow({ label, children }) {
  return (
    <div className={styles.prescreenExpand}>
      <p className={styles.prescreenExpandLabel}>
        {label}
      </p>

      <div className={styles.prescreenChips}>
        {children}
      </div>
    </div>
  );
}

function Chip({
  label,
  selected,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ava-text-body ${
        styles.prescreenChip
      } ${
        selected
          ? styles.prescreenChipActive
          : ""
      }`}
    >
      {label}
    </button>
  );
}